import {useEffect, useRef, useState} from 'react';
import {useLatest} from 'ahooks';
import type {EntityCardProps} from './entity-card.types';
import {ENTITY_CARD_REFRESH_INTERVAL_MS, ENTITY_CARD_REFRESH_TIMEOUT_MS, MAX_TIMER_DELAY_MS, MILLISECONDS_PER_SECOND} from './entity-card.types';
import {getEntityCardAction} from './actions/entity-card-action-registry';
import type {ClickEntity, EntityCardActionContext} from './actions/entity-card-action.types';
import {useAiChatActions} from '@/ai-chat/providers/ai-chat.context';
import {useAiChatMessage} from '@Components/ai-chat-window/providers/ai-chat-message.context';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {aiChatWindowActions} from '@Components/ai-chat-window/ai-chat-window.reducer';
import {MessageRole} from '@Components/ai-chat-window/ai-chat-window.types';
import {recordFeedback} from '@Libraries/ai-chat.library';

export function useRefreshedEntityCard(initialCard: EntityCardProps): EntityCardProps {
  const [card, setCard] = useState(initialCard);
  const isRequestInFlightRef = useRef(false);
  const fetchRefreshedCard = getEntityCardAction(card.entityType)?.fetchRefreshedCard;
  const entityRef = useLatest(card.entity ?? {});
  const hasRefreshMomentCome = useHasMomentCome(card.refreshAt);
  const isRefreshing = (card.refreshable === true || hasRefreshMomentCome) && card.entity !== undefined;

  useEffect((): (() => void) | undefined => {
    if (!isRefreshing || fetchRefreshedCard === undefined) {
      return undefined;
    }

    const startedAt = Date.now();
    const refreshCard = (): void => {
      if (isRequestInFlightRef.current) {
        return;
      }

      isRequestInFlightRef.current = true;
      void fetchRefreshedCard(entityRef.current)
        .then((refreshedCard): void => {
          if (refreshedCard === null) {
            return;
          }

          setCard(refreshedCard);
        })
        .catch((error: unknown): void => {
          console.error(error);
        })
        .finally((): void => {
          isRequestInFlightRef.current = false;
        });
    };

    refreshCard();
    const refreshTimer = window.setInterval((): void => {
      if (Date.now() - startedAt > ENTITY_CARD_REFRESH_TIMEOUT_MS) {
        window.clearInterval(refreshTimer);
        return;
      }

      refreshCard();
    }, ENTITY_CARD_REFRESH_INTERVAL_MS);

    return (): void => {
      window.clearInterval(refreshTimer);
    };
  }, [isRefreshing, fetchRefreshedCard, entityRef]);

  return card;
}

/** The chat-wide capabilities an entity's actions run with, for the message the entity is shown in. */
export function useEntityCardActionContext(): EntityCardActionContext {
  const actions = useAiChatActions();
  const {chatId, messageId} = useAiChatMessage();
  const dispatch = useAppDispatch();
  const message = useAppSelector((state) => state.aiChatWindow[chatId]?.messages?.[messageId]);

  let feedback = null;
  if (message?.role === MessageRole.ASSISTANT) {
    feedback = message.feedback;
  }

  return {
    openPdf: actions.openPdf,
    chatId,
    feedback,
    recordFeedback: (feedbackType): void => {
      void recordFeedback(chatId, messageId, feedbackType, null, null);

      if (message?.role !== MessageRole.ASSISTANT) {
        return;
      }

      dispatch(
        aiChatWindowActions.updateMessage({
          chatId,
          messageId,
          message: {...message, feedback: {type: feedbackType, comment: null, negativeFeedbackPreset: null}},
        })
      );
    },
  };
}

/** Runs an entity's click action, first pointing the user's next message at the entity when its action names one. */
export function useClickEntity(actionContext: EntityCardActionContext): ClickEntity {
  const {chatId, messageId} = useAiChatMessage();
  const dispatch = useAppDispatch();

  return (action, entity): void => {
    const entityId = action.pointedAtIdOf?.(entity) ?? '';
    if (entityId !== '' && messageId !== '') {
      dispatch(aiChatWindowActions.setPointedAtEntity({chatId, pointedAtEntity: {messageId, entityType: action.entityType, entityId}}));
    }

    void action.run(entity, actionContext);
  };
}

/**
 * Whether the moment (Unix seconds) has come, turning true when it does while the card is on screen; a card whose
 * moment changes, or goes, waits for its own.
 */
function useHasMomentCome(momentSeconds: number | undefined): boolean {
  const [cameMoment, setCameMoment] = useState<number | undefined>(() => (hasMomentPassed(momentSeconds) ? momentSeconds : undefined));

  useEffect((): (() => void) | undefined => {
    if (momentSeconds === undefined || cameMoment === momentSeconds) {
      return undefined;
    }

    const delay = momentSeconds * MILLISECONDS_PER_SECOND - Date.now();
    if (delay > MAX_TIMER_DELAY_MS) {
      return undefined;
    }

    const timer = window.setTimeout((): void => {
      setCameMoment(momentSeconds);
    }, Math.max(delay, 0));

    return (): void => {
      window.clearTimeout(timer);
    };
  }, [momentSeconds, cameMoment]);

  return momentSeconds !== undefined && cameMoment === momentSeconds;
}

function hasMomentPassed(momentSeconds: number | undefined): boolean {
  return momentSeconds !== undefined && momentSeconds * MILLISECONDS_PER_SECOND <= Date.now();
}
